// Serial numbers (IMEI, device serials): deterministic ids, parsing pasted
// lists, and warranty dates.
import { uuidV5 } from './batches'
import { CoreError, assertSafeInt } from './errors'

/** Namespace for serial ids (fixed; changing it would split existing serials). */
export const SERIAL_NAMESPACE = '3c1d9e57-8a4f-5b20-b6e1-7f0a2c9d4e18'

/** A serial as stored: trimmed, inner spaces removed, upper case. */
export function normalizeSerial(serial: string): string {
  return serial.replace(/\s+/g, '').toUpperCase()
}

/**
 * The id of one unit of a product. The same serial received on two devices
 * (even offline) gets the same id, so both upserts land on one row.
 */
export function serialId(tenantId: string, productId: string, serial: string): string {
  const s = normalizeSerial(serial)
  if (!s) throw new CoreError('invalid_input', 'a serial number is required')
  return uuidV5(SERIAL_NAMESPACE, `${tenantId}:${productId}:${s.toLowerCase()}`)
}

/**
 * Serials typed or scanned into a box: one per line (commas, semicolons and
 * tabs also separate). Returns them normalised, plus the ones given twice.
 */
export function parseSerials(text: string): { serials: string[]; duplicates: string[] } {
  const serials: string[] = []
  const duplicates: string[] = []
  const seen = new Set<string>()
  for (const part of text.split(/[\n\r,;\t]+/)) {
    const s = normalizeSerial(part)
    if (!s) continue
    if (seen.has(s)) {
      if (!duplicates.includes(s)) duplicates.push(s)
      continue
    }
    seen.add(s)
    serials.push(s)
  }
  return { serials, duplicates }
}

/**
 * Last day of a warranty of `months` starting on `saleDate` (YYYY-MM-DD):
 * the same day `months` later (end of month when it has no such day), minus one.
 */
export function warrantyUntil(saleDate: string, months: number): string {
  assertSafeInt(months, 'months')
  if (months <= 0) throw new CoreError('invalid_input', 'warranty months must be positive')
  const [y, m, d] = saleDate.split('-').map(Number) as [number, number, number]
  const target = m - 1 + months
  const year = y + Math.floor(target / 12)
  const month = target % 12
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const end = new Date(Date.UTC(year, month, Math.min(d, lastDay)))
  end.setUTCDate(end.getUTCDate() - 1)
  return end.toISOString().slice(0, 10)
}
